import type { Edicion, Plan, Renombre } from './api'

/**
 * «Así propongo organizar tu web» (plan del CMS dinámico §8.3), como lista que se revisa: cada
 * cosa del plan con su casilla (se queda o se quita) y lo que se puede renombrar. Las rutas son
 * las del núcleo (`rutaDe` en `estructura-ia/revisar.ts`): es con ellas con lo que el núcleo
 * entiende qué se quitó. Pura, para probarla sin navegador.
 */

export type CampoEditable = 'etiqueta' | 'nombre' | 'nombrePlural' | 'texto' | 'titulo'

export type Elemento = {
  ruta: string
  titulo: string
  detalle?: string
  /** Qué textos se pueden cambiar, con su valor de ahora. */
  editables: Array<{ campo: CampoEditable; etiqueta: string; valor: string }>
  hijos: Elemento[]
}

export type Seccion = { clave: string; titulo: string; ayuda: string; elementos: Elemento[] }

const ENTIDADES: Record<string, string> = { producto: 'los productos', entrada: 'las entradas del blog', servicio: 'los servicios', pagina: 'las páginas', termino: 'los términos' }
const UBICACIONES: Record<string, string> = { cabecera: 'Menú principal', pie: 'Pie de página', legal: 'Enlaces legales' }

const destinoEnLlano = (plan: Plan, destino: string): string => {
  if (destino === 'inicio') return 'la portada'
  const [clase, resto = ''] = destino.split(/:(.*)/s)
  if (clase === 'tipo') return `el listado de ${plan.tipos.find((t) => t.slug === resto)?.nombrePlural ?? resto}`
  if (clase === 'pagina') return `la página «${plan.paginas.find((p) => p.slug === resto)?.titulo ?? resto}»`
  if (clase === 'termino') return `«${resto.split('/')[1] ?? resto}»`
  return destino
}

/** `nombresDeTipo`: el nombre en llano de cada tipo de campo («Texto corto», «Número»), que da el núcleo con el plan. */
export const seccionesDelPlan = (plan: Plan, nombresDeTipo: Record<string, string> = {}): Seccion[] => {
  const secciones: Seccion[] = []
  if (plan.tipos.length) {
    secciones.push({
      clave: 'tipos',
      titulo: 'Tipos de contenido',
      ayuda: 'Cosas de la misma forma que irás añadiendo: cada una tiene su listado y su ficha.',
      elementos: plan.tipos.map((t) => ({
        ruta: `tipo:${t.slug}`,
        titulo: t.nombrePlural,
        detalle: [`En /${t.slug}`, t.taxonomias.length ? `se clasifican por ${t.taxonomias.map((x) => plan.taxonomias.find((y) => y.slug === x)?.nombrePlural ?? x).join(', ')}` : '', plan.diseno.vistasPorTipo.includes(t.slug) ? 'con su ficha diseñada con IA' : ''].filter(Boolean).join(' · '),
        editables: [
          { campo: 'nombre', etiqueta: 'En singular', valor: t.nombre },
          { campo: 'nombrePlural', etiqueta: 'En plural', valor: t.nombrePlural },
        ],
        hijos: [],
      })),
    })
  }
  if (plan.taxonomias.length) {
    secciones.push({
      clave: 'taxonomias',
      titulo: 'Clasificaciones (taxonomías)',
      ayuda: 'Cómo se agrupan y se filtran los contenidos. Cada término puede tener su página.',
      elementos: plan.taxonomias.map((t) => ({
        ruta: `taxonomia:${t.slug}`,
        titulo: t.nombrePlural,
        detalle: t.terminos.length ? undefined : 'Sin términos: los añadirás tú.',
        editables: [
          { campo: 'nombre', etiqueta: 'En singular', valor: t.nombre },
          { campo: 'nombrePlural', etiqueta: 'En plural', valor: t.nombrePlural },
        ],
        hijos: t.terminos.flatMap((x) => [
          { ruta: `termino:${t.slug}/${x.nombre}`, titulo: x.nombre, editables: [{ campo: 'nombre' as const, etiqueta: 'Nombre', valor: x.nombre }], hijos: [] },
          ...x.hijos.map((h) => ({ ruta: `termino:${t.slug}/${h}`, titulo: `${x.nombre} › ${h}`, editables: [{ campo: 'nombre' as const, etiqueta: 'Nombre', valor: h }], hijos: [] })),
        ]),
      })),
    })
  }
  if (plan.gruposDeCampos.length) {
    secciones.push({
      clave: 'grupos',
      titulo: 'Campos',
      ayuda: 'Lo que rellenarás en cada contenido, agrupado en pestañas del editor.',
      elementos: plan.gruposDeCampos.map((g) => ({
        ruta: `grupo:${g.nombre}`,
        titulo: g.nombre,
        detalle: g.entidad === 'contenido' ? `En ${plan.tipos.find((t) => t.slug === g.valor)?.nombrePlural ?? g.valor}` : g.entidad === 'termino' ? `En los términos de ${g.valor}` : `En ${ENTIDADES[g.entidad] ?? g.entidad}`,
        editables: [{ campo: 'nombre', etiqueta: 'Nombre', valor: g.nombre }],
        hijos: g.campos.map((c) => ({
          ruta: `campo:${g.nombre}/${c.clave}`,
          titulo: c.etiqueta,
          detalle: `${nombresDeTipo[c.tipo] ?? c.tipo}${c.obligatorio ? ' · obligatorio' : ''}${c.campos?.length ? ` · ${c.campos.map((x) => x.etiqueta).join(', ')}` : ''}`,
          editables: [{ campo: 'etiqueta' as const, etiqueta: 'Etiqueta', valor: c.etiqueta }],
          hijos: [],
        })),
      })),
    })
  }
  if (plan.paginas.length) {
    secciones.push({
      clave: 'paginas',
      titulo: 'Páginas nuevas',
      ayuda: 'Se crean en borrador, con lo que falta marcado «por rellenar».',
      elementos: plan.paginas.map((p) => ({ ruta: `pagina:${p.slug}`, titulo: p.titulo, detalle: `/${p.slug}`, editables: [{ campo: 'titulo', etiqueta: 'Título', valor: p.titulo }], hijos: [] })),
    })
  }
  const menus = (['cabecera', 'pie', 'legal'] as const).flatMap((u) =>
    plan.menus[u].map((e, i) => ({ ruta: `menu:${u}/${i}`, titulo: e.texto, detalle: `${UBICACIONES[u]} → ${destinoEnLlano(plan, e.destino)}`, editables: [{ campo: 'texto' as const, etiqueta: 'Texto', valor: e.texto }], hijos: [] })),
  )
  if (menus.length) secciones.push({ clave: 'menus', titulo: 'Enlaces en los menús', ayuda: 'Se añaden a los menús de tu web; no se quita ninguno de los que ya tienes.', elementos: menus })
  if (plan.contenidoInicial.length) {
    secciones.push({
      clave: 'contenidos',
      titulo: 'Contenidos de ejemplo',
      ayuda: 'En borrador y marcados «por rellenar»: para que veas cómo queda. Los cambias o los borras.',
      elementos: plan.contenidoInicial.map((c, i) => ({ ruta: `contenido:${i}`, titulo: c.titulo, detalle: plan.tipos.find((t) => t.slug === c.tipo)?.nombre ?? c.tipo, editables: [], hijos: [] })),
    })
  }
  return secciones
}

/** Lo que se le manda al núcleo al aceptar: lo quitado y solo los nombres que de verdad cambiaron. */
export const edicionDe = (plan: Plan, quitados: ReadonlySet<string>, renombres: Record<string, Renombre>): Edicion => {
  const originales = new Map(
    seccionesDelPlan(plan)
      .flatMap((s) => s.elementos.flatMap((e) => [e, ...e.hijos]))
      .map((e) => [e.ruta, Object.fromEntries(e.editables.map((x) => [x.campo, x.valor]))] as const),
  )
  const renombrar: Record<string, Renombre> = {}
  for (const [ruta, cambios] of Object.entries(renombres)) {
    if (quitados.has(ruta)) continue
    const antes = originales.get(ruta) ?? {}
    const distintos = Object.fromEntries(Object.entries(cambios).filter(([campo, valor]) => typeof valor === 'string' && valor.trim() && valor.trim() !== antes[campo]).map(([c, v]) => [c, (v as string).trim()]))
    if (Object.keys(distintos).length) renombrar[ruta] = distintos
  }
  return { quitar: [...quitados], renombrar }
}

/** Lo que arrastra quitar algo (para avisar antes): los grupos, los enlaces y los ejemplos de un tipo. */
export const loQueArrastra = (plan: Plan, ruta: string): string[] => {
  const [clase, resto = ''] = ruta.split(/:(.*)/s)
  if (clase === 'tipo') {
    return [
      ...plan.gruposDeCampos.filter((g) => g.entidad === 'contenido' && g.valor === resto).map((g) => `los campos «${g.nombre}»`),
      ...(['cabecera', 'pie', 'legal'] as const).flatMap((u) => plan.menus[u].filter((e) => e.destino === `tipo:${resto}`).map((e) => `el enlace «${e.texto}»`)),
      ...plan.contenidoInicial.filter((c) => c.tipo === resto).map((c) => `el ejemplo «${c.titulo}»`),
    ]
  }
  if (clase === 'taxonomia') return plan.tipos.filter((t) => t.taxonomias.includes(resto)).map((t) => `la clasificación de ${t.nombrePlural}`)
  if (clase === 'pagina') return (['cabecera', 'pie', 'legal'] as const).flatMap((u) => plan.menus[u].filter((e) => e.destino === `pagina:${resto}`).map((e) => `el enlace «${e.texto}»`))
  return []
}

export type Diferencia = { ruta: string; que: 'cambiar' | 'crear'; etiqueta: string; detalle: string[] }

/**
 * «Lo que cambia en tu web» con lo que la persona ha hecho en esta misma pantalla: lo quitado no
 * sale, y lo renombrado sale con su nombre nuevo. Las diferencias las calcula el núcleo con el plan
 * de la propuesta; aquí solo se les aplica la edición, que es lo que viajará al aceptar.
 */
export const diferenciasConEdicion = (diferencias: Diferencia[], plan: Plan, quitados: ReadonlySet<string>, renombres: Record<string, Renombre>): Diferencia[] => {
  const nuevo = (ruta: string, campo: keyof Renombre): string | undefined => {
    const valor = renombres[ruta]?.[campo]?.trim()
    return valor ? valor : undefined
  }
  // El nombre que cada diferencia lleva entre comillas, por clase de ruta.
  const campoDe: Record<string, keyof Renombre> = { tipo: 'nombrePlural', taxonomia: 'nombrePlural', grupo: 'nombre', pagina: 'titulo' }
  const salida: Diferencia[] = []
  for (const d of diferencias) {
    if (quitados.has(d.ruta)) continue
    // Un tipo quitado se lleva sus campos: el grupo que solo existía para él tampoco cambia nada.
    if (d.ruta.startsWith('grupo:') && [...quitados].some((q) => q.startsWith('tipo:') && plan.gruposDeCampos.some((g) => `grupo:${g.nombre}` === d.ruta && g.entidad === 'contenido' && `tipo:${g.valor}` === q))) continue
    const clase = d.ruta.slice(0, d.ruta.indexOf(':'))
    let etiqueta = d.etiqueta
    const campo = campoDe[clase]
    const nombre = campo ? nuevo(d.ruta, campo) : undefined
    if (nombre) etiqueta = etiqueta.replace(/«[^»]*»/, `«${nombre}»`)
    let detalle = d.detalle
    if (clase === 'menu') {
      const ubicacion = d.ruta.slice(5) as keyof Plan['menus']
      const textos = (plan.menus[ubicacion] ?? []).flatMap((e, i) => (quitados.has(`menu:${ubicacion}/${i}`) ? [] : [nuevo(`menu:${ubicacion}/${i}`, 'texto') ?? e.texto]))
      if (!textos.length) continue
      detalle = [`Se añaden: ${textos.join(', ')}`]
    }
    if (d.ruta === 'contenido') {
      const titulos = plan.contenidoInicial.flatMap((c, i) => (quitados.has(`contenido:${i}`) || quitados.has(`tipo:${c.tipo}`) ? [] : [c.titulo]))
      if (!titulos.length) continue
      etiqueta = `${titulos.length} ${titulos.length === 1 ? 'contenido' : 'contenidos'} de ejemplo, por rellenar`
      detalle = titulos
    }
    salida.push({ ...d, etiqueta, detalle })
  }
  return salida
}
